import React, { useState, useRef, useEffect } from "react";
import { Button, Box, Text } from "@primer/react";
import Dialog from "../../../components/ui/modal/SafeDialog";
import { UploadIcon, XIcon } from "@primer/octicons-react";
import { hocSinhApi } from "../../../api/hocSinhApi";
import styled from "styled-components";
import UserAvatar from "../../../components/user-avatar";

// Styled Components
const Container = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
`;

const AvatarContainer = styled.div`
  cursor: pointer;
  margin-right: 12px;
`;

const Avatar = styled.div<{ imageUrl: string }>`
  background-image: url(${(props) => props.imageUrl});
  width: 50px;
  height: 50px;
  background-size: cover;
  border-radius: 50%;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StudentName = styled(Text)`
  font-weight: bold;
  margin-bottom: 4px;
`;

const StudentId = styled(Text)`
  color: #666;
  font-size: 14px;
`;

const DropZone = styled.div`
  border: 2px dashed #d0d7de;
  border-radius: 6px;
  padding: 32px;
  text-align: center;
  margin-bottom: 16px;
  cursor: pointer;
  background-color: #f6f8fa;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #eaeef2;
  }
`;

const PreviewContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const PreviewImage = styled.img`
  max-width: 200px;
  max-height: 200px;
  border-radius: 4px;
`;

const RemoveButton = styled(Button)`
  position: absolute;
  top: -10px;
  right: -10px;
`;

const UploadText = styled(Text)`
  display: block;
  margin-top: 8px;
  color: #57606a;
`;

const SubText = styled(Text)`
  font-size: 12px;
  color: #57606a;
  margin-top: 4px;
`;

const ButtonContainer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;
const StyledDialog = styled(Dialog)`
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  max-width: 90vw;
  width: 480px !important;
  margin: 0 !important;
`;

const AvatarWrapper = styled.div`
  position: relative;
  width: 50px;
  height: 50px;
  &:hover .avatar-overlay {
    opacity: 1;
  }
`;

const AvatarOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
`;

interface HocSinhProps {
  ho_ten?: string;
  ma_hs?: string;
  ma_tn?: string;
  avatar?: string;
  dm_coso_id?: number;
  ten_lop?: string;
}

interface HocSinhNameAndAvatarProps {
  hocSinh: HocSinhProps;
}
interface UploadAvatarResponse {
  status: string;
  avatar: string;
}
const HocSinhNameAndAvatar: React.FC<HocSinhNameAndAvatarProps> = ({
  hocSinh,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [currentAvatar, setCurrentAvatar] = useState(hocSinh.avatar);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync avatar khi hocSinh.avatar thay đổi
  useEffect(() => {
    setCurrentAvatar(hocSinh.avatar);
  }, [hocSinh.avatar]);

  const handleAvatarClick = () => {
    setIsOpen(true);
  };

  const resetState = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setIsUploading(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        // 2MB limit
        alert("File size must be less than 2MB");
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  interface UploadAvatarResponse {
    status: string;
    avatar: string;
  }

  const handleUpload = async () => {
    if (!selectedFile || !hocSinh.dm_coso_id || !hocSinh.ma_hs) return;

    setIsUploading(true);
    try {
      const response = await hocSinhApi.uploadAvatar(
        selectedFile,
        hocSinh.dm_coso_id,
        hocSinh.ma_hs
      );
      // Cast response trực tiếp
      const result = response as any;

      if (result.status === "success") {
        setCurrentAvatar(result.avatar);
        resetState();
        setIsOpen(false);
      }
    } catch (error) {
      console.error("Error uploading avatar:", error);
      alert("Failed to upload avatar. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert("File size must be less than 2MB");
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Container>
      <AvatarContainer>
        <AvatarWrapper>
          {/* <Avatar imageUrl={currentAvatar || "../../../images/hs_avatar.png"} /> */}

          <UserAvatar fullName={hocSinh.ho_ten} url={currentAvatar} size="xxlarge" />

          <AvatarOverlay className="avatar-overlay" onClick={handleAvatarClick}>
            <UploadIcon fill="white" size={20} />
          </AvatarOverlay>
        </AvatarWrapper>
      </AvatarContainer>
      <InfoContainer>
        <StudentName>{hocSinh.ho_ten}</StudentName>
        <StudentId>{hocSinh.ma_hs || hocSinh.ma_tn} - {hocSinh.ten_lop}</StudentId>
      </InfoContainer>

      <StyledDialog
        isOpen={isOpen}
        onDismiss={() => {
          setIsOpen(false);
          resetState();
        }}
        aria-labelledby="header-id"
      >
        <Dialog.Header id="header-id">
          <Text as="h3" style={{ margin: 0 }}>
            Cập nhật Avatar
          </Text>
        </Dialog.Header>

        <Box padding={4}>
          <DropZone
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {previewUrl ? (
              <PreviewContainer>
                <PreviewImage src={previewUrl} alt="Preview" />
                <RemoveButton
                  variant="danger"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetState();
                  }}
                >
                  <XIcon />
                </RemoveButton>
              </PreviewContainer>
            ) : (
              <>
                <UploadIcon size={32} />
                <UploadText>Kéo thả ảnh vào đây hoặc click để chọn</UploadText>
                <SubText>Hỗ trợ: JPG, PNG (tối đa 2MB)</SubText>
              </>
            )}
          </DropZone>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          <ButtonContainer>
            <Button
              variant="invisible"
              onClick={() => {
                setIsOpen(false);
                resetState();
              }}
            >
              Hủy
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? "Đang tải..." : "Tải lên"}
            </Button>
          </ButtonContainer>
        </Box>
      </StyledDialog>
    </Container>
  );
};

// Custom comparison function để tránh re-render không cần thiết
const areEqual = (prevProps: HocSinhNameAndAvatarProps, nextProps: HocSinhNameAndAvatarProps) => {
  const prev = prevProps.hocSinh;
  const next = nextProps.hocSinh;

  return (
    prev.ho_ten === next.ho_ten &&
    prev.ma_hs === next.ma_hs &&
    prev.ma_tn === next.ma_tn &&
    prev.avatar === next.avatar &&
    prev.dm_coso_id === next.dm_coso_id &&
    prev.ten_lop === next.ten_lop
  );
};

export default React.memo(HocSinhNameAndAvatar, areEqual);
